import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import { AgentPageLayout } from '../components/layout/AgentPageLayout';
import { createAgentBroadcast, fetchAgentPlayers, fetchDashboard, fetchShops } from '../services/api';
import type { AgentDashboardData, AgentPlayer, AgentShop } from '../types';
import { PAGE } from '@kioskgaming/ui';

const TITLE_MAX = 500;
const BODY_MAX = 1000;

type TargetKind = 'shop' | 'player';

export default function SendBroadcastPage() {
  const [dash, setDash] = useState<AgentDashboardData | null>(null);
  const [shops, setShops] = useState<AgentShop[]>([]);
  const [players, setPlayers] = useState<AgentPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [targetKind, setTargetKind] = useState<TargetKind>('shop');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s, p] = await Promise.all([fetchDashboard(), fetchShops(), fetchAgentPlayers()]);
      if (d.success && d.data) setDash(d.data);
      if (s.success && s.data) setShops(s.data.filter((x) => x.status === 'active'));
      if (p.success && p.data) setPlayers(p.data.filter((x) => x.status === 'active'));
    } catch {
      toast.error('Could not load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dash?.can_delegate_popups) return;
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (selected.size === 0) {
      toast.error(`Select at least one ${targetKind === 'shop' ? 'shop' : 'player'}`);
      return;
    }
    setSubmitting(true);
    try {
      const selectedIds = Array.from(selected);
      const res = await createAgentBroadcast({
        title: title.trim(),
        body: body.trim(),
        targetShopIds: targetKind === 'shop' ? selectedIds : [],
        targetUserIds: targetKind === 'player' ? selectedIds : [],
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      if (res.success) {
        toast.success('Broadcast sent');
        setTitle('');
        setBody('');
        setExpiresAt('');
        setSelected(new Set());
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <AgentPageLayout>
          <PageLoadingIndicator variant="page" />
        </AgentPageLayout>
      </AppLayout>
    );
  }

  if (dash && !dash.can_delegate_popups) {
    return <Navigate to="/" replace />;
  }

  const items = targetKind === 'shop' ? shops : players;

  return (
    <AppLayout>
      <AgentPageLayout
        title={PAGE.broadcasts}
        description="Send a bell notification broadcast to Shops or Players under your Agent account. It uses the same permission as Popups."
      >
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 space-y-2">
          <p className="text-sm font-medium text-gray-800">Audience</p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-broadcast-target"
              checked={targetKind === 'shop'}
              onChange={() => {
                setTargetKind('shop');
                setSelected(new Set());
              }}
            />
            <span>Shops</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-broadcast-target"
              checked={targetKind === 'player'}
              onChange={() => {
                setTargetKind('player');
                setSelected(new Set());
              }}
            />
            <span>Players</span>
          </label>
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className="text-sm font-medium text-gray-700">Title</label>
            <span className="text-xs text-gray-500 tabular-nums">
              {title.length}/{TITLE_MAX}
            </span>
          </div>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
            maxLength={TITLE_MAX}
            required
          />
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className="text-sm font-medium text-gray-700">Message</label>
            <span className="text-xs text-gray-500 tabular-nums">
              {body.length}/{BODY_MAX}
            </span>
          </div>
          <textarea
            className="w-full min-h-[140px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            maxLength={BODY_MAX}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hide after (optional)</label>
          <input
            type="datetime-local"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">{targetKind === 'shop' ? 'Shops' : 'Players'}</p>
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">No active {targetKind === 'shop' ? 'shops' : 'players'}.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
              {items.map((it) => (
                <label
                  key={it.id}
                  className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                >
                  <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} />
                  {targetKind === 'shop' ? (
                    <>
                      <span className="font-medium text-gray-900">{(it as AgentShop).name}</span>
                      <span className="text-gray-500 truncate">{(it as AgentShop).email}</span>
                    </>
                  ) : (
                    <span className="font-medium text-gray-900 truncate">
                      {(it as AgentPlayer).phone || (it as AgentPlayer).email || (it as AgentPlayer).username || it.id}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={submitting || items.length === 0}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {submitting ? 'Sending…' : 'Send broadcast'}
        </button>
      </form>
      </AgentPageLayout>
    </AppLayout>
  );
}
